import React, { useCallback, useEffect, useState } from "react";
import { Film, Flame, Clock, Star, Loader2, Video, Trophy, Crown } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ClipCard } from "@/components/CommunityClips";
import { Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";

const PAGE = 16;

const TABS = [
  { key: "trending", label: "Trending", icon: Flame, featured: false },
  { key: "recent", label: "Recent", icon: Clock, featured: false },
  { key: "featured", label: "Staff Picks", icon: Star, featured: true },
];

export default function ClipsFeed() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("trending");
  const [clips, setClips] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [board, setBoard] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [pinned, setPinned] = useState(null);

  const pinnedId = searchParams.get("clip");
  useEffect(() => {
    if (!pinnedId) { setPinned(null); return; }
    setPinned(undefined);
    api.get(`/clips/${pinnedId}/meta`).then(({ data }) => setPinned(data)).catch(() => setPinned(null));
  }, [pinnedId]);

  useEffect(() => {
    api.get("/clips/leaderboard?days=7&limit=5")
      .then(({ data }) => setBoard(data.clips || []))
      .catch(() => setBoard([]));
    api.get("/clips/squad-highlights?limit=10")
      .then(({ data }) => setHighlights(data.clips || []))
      .catch(() => setHighlights([]));
  }, []);

  const fetchPage = useCallback(async (key, offset) => {
    const t = TABS.find((x) => x.key === key) || TABS[0];
    const params = new URLSearchParams({
      sort: t.featured ? "trending" : key,
      limit: String(PAGE),
      offset: String(offset),
    });
    if (t.featured) params.set("featured", "true");
    const { data } = await api.get(`/clips?${params.toString()}`);
    return data;
  }, []);

  const loadFirst = useCallback(async (key) => {
    setClips(null);
    try {
      const data = await fetchPage(key, 0);
      setClips(data.clips || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch { setClips([]); setTotal(0); setHasMore(false); }
  }, [fetchPage]);

  useEffect(() => { loadFirst(tab); }, [tab, loadFirst]);

  const loadMore = async () => {
    if (loadingMore || !clips) return;
    setLoadingMore(true);
    try {
      const data = await fetchPage(tab, clips.length);
      setClips((prev) => [...(prev || []), ...(data.clips || [])]);
      setHasMore(!!data.has_more);
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-7xl mx-auto" data-testid="clips-feed-page">
      <div className="flex items-center gap-3 mb-2">
        <Film className="w-8 h-8 text-fuchsia-400" />
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Community Clips</h1>
      </div>
      <p className="text-[hsl(var(--muted-foreground))] mb-6">
        The best AI-built gameplay moments, captured &amp; shared by players — sound on. {total > 0 && <span className="font-semibold text-[hsl(var(--foreground))]">{total.toLocaleString()} clips</span>}
      </p>

      {board && board.length > 0 && (
        <div className="mb-8" data-testid="clips-leaderboard">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" /> This Week&apos;s Best Plays
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Top clips from the last 7 days — most-liked wins. Publish yours to climb the board.</p>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" data-testid="clips-leaderboard-row">
            {board.map((c) => <ClipCard key={c.id} clip={c} rank={c.rank} linkBase="/app/arcade" />)}
          </div>
        </div>
      )}

      {pinned && (
        <div className="mb-8 rounded-2xl border border-fuchsia-500/40 bg-fuchsia-500/5 p-4" data-testid="clips-pinned">
          <h2 className="font-display text-xl font-bold flex items-center gap-2 mb-1"><Flame className="w-5 h-5 text-fuchsia-400" /> Your highlighted clip</h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Someone reacted 🔥 to this — here it is.</p>
          <div className="max-w-sm"><ClipCard clip={pinned} linkBase="/app/arcade" /></div>
        </div>
      )}

      {highlights && highlights.length > 0 && (
        <div className="mb-8" data-testid="clips-squad-highlights">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Crown className="w-5 h-5 text-fuchsia-400" /> Squad Highlights
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">Boss-MVP final-blow moments from co-op raids — hover to preview, or tap ⤢ to watch fullscreen with sound.</p>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1" data-testid="clips-squad-highlights-row">
            {highlights.map((c) => <ClipCard key={c.id} clip={c} linkBase="/app/arcade" hoverPlay soundToggle theater />)}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6" data-testid="clips-feed-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`clips-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${active ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--muted))]"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {!clips ? (
        <Spinner />
      ) : clips.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] bg-[hsl(var(--card))] p-10 text-center" data-testid="clips-feed-empty">
          <Video className="w-10 h-10 mx-auto text-fuchsia-400 mb-3" />
          <p className="font-semibold text-lg mb-1">No clips here yet</p>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5 max-w-md mx-auto">
            Play any published game, hit <span className="font-semibold text-fuchsia-400">Record clip</span>, and publish your best moment to the community feed.
          </p>
          <Button onClick={() => navigate("/app/play")} className="rounded-full" data-testid="clips-feed-play-cta">
            Browse games to play
          </Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="clips-feed-grid">
            {clips.map((c) => <ClipCard key={c.id} clip={c} grid linkBase="/app/arcade" />)}
          </div>
          {hasMore && (
            <div className="flex justify-center mt-8">
              <Button variant="outline" onClick={loadMore} disabled={loadingMore} className="rounded-full gap-2" data-testid="clips-feed-load-more">
                {loadingMore ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</> : "Load more clips"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
